import React, { useEffect, useMemo, useRef, useState } from "react";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import styles from "./machine-name-dropdown.module.scss";
import clearIcon from "@src/assets/svg/close-icon.svg";
import caretDownIcon from "@src/assets/svg/caret-down.svg";
import { MachineNameAndSourceId } from "@common/types/server-list-types";
import SpinnerWithVariant from "../SpinnerWithVariant/spinner-with-variant";

const cssClassName = "machine-name-dropdown";

interface MachineNameDropdownProps {
  options: MachineNameAndSourceId[];
  selectedMachine: MachineNameAndSourceId | null;
  onSelectMachine: (machine: MachineNameAndSourceId | null) => void;
  machineLabelText: string;
  noMachineNamesText: string;
  placeholderText: string;
  isLoading: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  showDropdownToggle?: boolean;
}

export const MachineNameDropdown = ({
  options,
  selectedMachine,
  onSelectMachine,
  machineLabelText,
  noMachineNamesText,
  placeholderText,
  isLoading,
  disabled = false,
  hideLabel = false,
  showDropdownToggle = false,
}: MachineNameDropdownProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
      setInputValue("");
    }
  }, [disabled]);

  const closeDropdown = () => {
    setIsOpen(false);
    setInputValue("");
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleInputChange = (value: string) => {
    if (!disabled) {
      setInputValue(value);
      setIsOpen(true);
    }
  };

  useClickOutsideListener(containerRef, closeDropdown, isOpen);

  const filteredOptions = useMemo(() => {
    const normalized = inputValue.trim().toLowerCase();
    if (!normalized) {
      return options;
    }

    return options.filter(
      (option) =>
        (option.name || "").toLowerCase().includes(normalized) ||
        (option.source_id || "").toLowerCase().includes(normalized),
    );
  }, [inputValue, options]);

  const handleMachineSelect = (machine: MachineNameAndSourceId) => {
    onSelectMachine(machine);
    setInputValue("");
    setIsOpen(false);
  };

  const handleRemoveMachine = () => {
    onSelectMachine(null);
    setInputValue("");
    setIsOpen(true);
  };

  const renderDropdownContent = () => {
    if (disabled) {
      return null;
    }
    if (isLoading) {
      return (
        <div className={styles[`${cssClassName}__loading-item`]}>
          <SpinnerWithVariant variant="small" />
        </div>
      );
    }

    if (filteredOptions.length === 0) {
      return (
        <div className={styles[`${cssClassName}__item`]}>
          {noMachineNamesText}
        </div>
      );
    }

    return filteredOptions.map((machine) => (
      <button
        key={machine.source_id}
        type="button"
        className={styles[`${cssClassName}__item`]}
        onClick={() => handleMachineSelect(machine)}
      >
        {machine.name}
      </button>
    ));
  };

  const renderSelectedMachine = () => (
    <span className={styles[`${cssClassName}__tag`]}>
      {selectedMachine?.name}
      {!disabled && (
        <button
          type="button"
          className={styles[`${cssClassName}__tag-remove`]}
          onClick={handleRemoveMachine}
        >
          <img
            src={clearIcon}
            alt="Remove"
            className={styles[`${cssClassName}__tag-remove-icon`]}
          />
        </button>
      )}
    </span>
  );

  const renderInput = () => (
    <input
      className={styles[`${cssClassName}__input`]}
      placeholder={placeholderText}
      value={inputValue}
      disabled={disabled}
      onFocus={handleInputFocus}
      onChange={(e) => handleInputChange(e.target.value)}
    />
  );

  return (
    <div ref={containerRef} className={styles[cssClassName]}>
      {!hideLabel && (
        <label className={styles[`${cssClassName}__label`]}>
          {machineLabelText}
        </label>
      )}

      <div className={styles[`${cssClassName}__input-wrapper`]}>
        {selectedMachine ? renderSelectedMachine() : renderInput()}
        {showDropdownToggle && (
          <button
            type="button"
            className={styles[`${cssClassName}__dropdown-toggle`]}
            aria-label={placeholderText}
            aria-expanded={isOpen}
            onClick={() => setIsOpen((open) => !open)}
            disabled={disabled}
          >
            <img
              src={caretDownIcon}
              alt=""
              aria-hidden="true"
              style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            />
          </button>
        )}
      </div>
      {isOpen && !disabled && (
        <div className={styles[`${cssClassName}__list`]}>
          {renderDropdownContent()}
        </div>
      )}
    </div>
  );
};
